import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useBuilderStore } from '../stores/builderStore';
import { getForm, updateForm, publishForm } from '../lib/api';
import Builder from '../components/builder/Builder';

export default function FormBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const { initializeForm, formName, formSchema, hasUnsavedChanges, markAsSaved, reset } = useBuilderStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPublishSuccess, setShowPublishSuccess] = useState<{ publicUrl: string; resultsUrl: string } | null>(null);

  useEffect(() => {
    if (!id) return;

    async function loadForm() {
      try {
        setLoading(true);
        const form = await getForm(id!);
        initializeForm(form.id, form.name, form.schema);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load form');
      } finally {
        setLoading(false);
      }
    }

    loadForm();

    return () => {
      reset();
    };
  }, [id, initializeForm, reset]);

  async function handleSave() {
    if (!id) return;

    try {
      setSaving(true);
      setError(null);
      await updateForm(id, { schema: formSchema });
      markAsSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save form');
    } finally {
      setSaving(false);
    }
  }

  async function handlePublish() {
    if (!id) return;

    if (formSchema.fields.length === 0) {
      setError('Add at least one field before publishing');
      return;
    }

    try {
      setPublishing(true);
      setError(null);

      // Save first
      if (hasUnsavedChanges) {
        await updateForm(id, { schema: formSchema });
        markAsSaved();
      }

      const result = await publishForm(id);
      setShowPublishSuccess({ publicUrl: result.publicUrl, resultsUrl: result.resultsUrl });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish form');
    } finally {
      setPublishing(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error && !formName) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Link to="/admin" className="btn-primary">
            Back to Forms
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin" className="text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </Link>
          <h1 className="text-lg font-semibold text-gray-900">{formName}</h1>
          {hasUnsavedChanges && (
            <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
              Unsaved changes
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          {error && (
            <span className="text-sm text-red-600">{error}</span>
          )}

          <button
            onClick={handleSave}
            disabled={saving || !hasUnsavedChanges}
            className="btn-secondary"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>

          <button
            onClick={handlePublish}
            disabled={publishing || formSchema.fields.length === 0}
            className="btn-primary"
          >
            {publishing ? 'Publishing...' : 'Publish'}
          </button>
        </div>
      </header>

      {/* Builder */}
      <Builder />

      {/* Publish Success Modal */}
      {showPublishSuccess && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card p-6 w-full max-w-md mx-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Form Published!</h2>
              <p className="text-gray-500">Your form is now live and accepting submissions</p>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="text-sm text-gray-500 block mb-1">Public Form URL</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={window.location.origin + showPublishSuccess.publicUrl}
                    readOnly
                    className="input flex-1 font-mono text-sm"
                  />
                  <button
                    onClick={() => navigator.clipboard.writeText(window.location.origin + showPublishSuccess.publicUrl)}
                    className="btn-secondary"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4">
                <label className="text-sm text-blue-600 block mb-1">Results Viewer URL</label>
                <p className="text-xs text-blue-500 mb-2">Share this link to allow others to view form submissions</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={window.location.origin + showPublishSuccess.resultsUrl}
                    readOnly
                    className="input flex-1 font-mono text-sm"
                  />
                  <button
                    onClick={() => navigator.clipboard.writeText(window.location.origin + showPublishSuccess.resultsUrl)}
                    className="btn-secondary"
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowPublishSuccess(null)}
                className="btn-secondary flex-1"
              >
                Close
              </button>
              <a
                href={showPublishSuccess.publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary flex-1 text-center"
              >
                View Form
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
